import React from 'react';
import { Redirect, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import * as PropTypes from 'prop-types';
import ViaRoute from './ViaRoute';
import routeLocations from '../../utility/route-locations';
import { selectSessionUser, selectSessionUserRequest, selectIsInitialised } from '../../state/ui/application/sessionUiReducer';
import { requestCurrentSessionUser } from '../../state/ui/application/sessionUiActions';


// Check if the session user has accepted all the terms
const allTermsAcceptedOnServer = (sessionUser) => {
  if (!sessionUser) throw new Error('Checking if session user has accepted terms, but session user is not loaded.');

  const termsAcceptedOnServer = sessionUser.termsToAccept.termsOfUseToAccept === null;
  const privacyAcceptedOnServer = sessionUser.termsToAccept.privacyPolicyToAccept === null;
  const eulaAcceptedOnServer = sessionUser.termsToAccept.eulaToAccept === null;
  return termsAcceptedOnServer && privacyAcceptedOnServer && eulaAcceptedOnServer;
};


/**
 * A custom hook which is used for initialising required session data for
 * secured areas of the application.
 */
export function useSessionBootstrap() {
  // Redux State
  const userRequest = useSelector(selectSessionUserRequest);
  const isSessionInitialised = useSelector(selectIsInitialised);
  const dispatch = useDispatch();

  // Load the session user if we haven't loaded / started loading the session data yet.
  if (!isSessionInitialised && !userRequest) dispatch(requestCurrentSessionUser());

  return isSessionInitialised;
}


/**
 * Checks the authenticator if the client has access to the secured section of the application.
 * (Checks that they have logged in and that they have accepted the terms and conditions).
 * If the user does not have access, then the login screen is displayed instead.
 */
export default function SecuredRoute({
  component: Component, ...rest
}) {
  const isSessionInitialsed = useSessionBootstrap();
  const sessionUser = useSelector(selectSessionUser);
  const location = useLocation();

  // Still loading. Render nothing.
  if (!isSessionInitialsed) return <></>;

  // The user is not authenticated yet, or we need them to acknowledge the T&C's.
  // Both scenarios are handled by the login component.
  if (!sessionUser || !allTermsAcceptedOnServer(sessionUser)) {
    // TODO: We should be displaying a notification: 'Please sign in to access'
    // TODO: message.warning('You must sign in to access this page.');
    return <Redirect to={{ pathname: routeLocations.login, state: { sourceLocation: location.pathname } }} />;
  }

  // All session data is loaded. Let the user see the route they were accessing.
  return <ViaRoute {...rest} render={props => (<Component {...props} />)} />;
}

// Validate the prop type parameters.
SecuredRoute.propTypes = {
  component: PropTypes.any.isRequired, // eslint-disable-line react/forbid-prop-types
};
